import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import supabase from '../config/supabaseClient';
import { useTheme } from '../components/ThemeContext';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filteredEvents, setFilteredEvents] = useState([]);
  const { theme } = useTheme();

  useEffect(() => {
    async function fetchEvents() {
      setLoading(true);
      const { data, error } = await supabase.from('etwic_events').select('*').order('created_at', { ascending: false });
      if (!error) setEvents(data || []);
      setLoading(false);
    }
    fetchEvents();
  }, []);

  useEffect(() => {
    // Filter events by search
    if (!search.trim()) {
      setFilteredEvents(events);
    } else {
      const s = search.trim().toLowerCase();
      setFilteredEvents(
        events.filter(event =>
          (event.title && event.title.toLowerCase().includes(s)) ||
          (event.location && event.location.toLowerCase().includes(s)) ||
          (event.short_description && event.short_description.toLowerCase().includes(s)) ||
          (event.full_description && event.full_description.toLowerCase().includes(s))
        )
      );
    }
  }, [search, events]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Filtering is handled by useEffect
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme === 'dark' ? '#18181b' : 'linear-gradient(135deg, #f8fafc 0%, #fff7f0 100%)',
      color: theme === 'dark' ? '#fff' : undefined,
      padding: '0',
    }}>
      {/* Hero Image */}
      <div style={{
        width: '100%',
        height: 320,
        overflow: 'hidden',
        marginTop: '2rem',
        marginBottom: 32,
        borderBottomLeftRadius: 24,
        borderBottomRightRadius: 24,
        boxShadow: '0 4px 24px 0 #ff66001a',
        background: theme === 'dark' ? '#23232b' : undefined,
      }}>
        <img
          src={'/events.jpg'}
          alt="Events Hero"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            filter: theme === 'dark' ? 'brightness(0.7)' : undefined,
          }}
        />
      </div>
      <div style={{
        maxWidth: 1200,
        margin: '0 auto',
        padding: '3rem 1rem 2rem 1rem',
      }}>
        {/* Page Title */}
        <h1 style={{
          textAlign: 'center',
          color: '#FF6600',
          fontWeight: 900,
          fontSize: '2.5rem',
          marginBottom: '2.5rem',
          letterSpacing: '-1px',
        }}>
          Events
        </h1>
        {/* Top Search Bar (no view toggles) */}
        <form
          onSubmit={handleSearch}
          style={{
            background: theme === 'dark' ? '#23232b' : '#fff',
            borderRadius: 6,
            boxShadow: theme === 'dark' ? '0 1px 8px 0 #111' : '0 1px 8px 0 #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            padding: '1.2rem 2rem',
            marginBottom: 36,
            gap: 24,
            flexWrap: 'wrap',
          }}
        >
          {/* Search Input */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 220,
            background: theme === 'dark' ? '#23232b' : '#fff',
            borderRadius: 4,
            border: theme === 'dark' ? '1px solid #333' : '1px solid #e5e7eb',
            padding: '0.5rem 1rem',
            gap: 8,
          }}>
            <svg width="20" height="20" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            <input
              type="text"
              placeholder="Search for events"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                fontSize: '1.08rem',
                background: 'transparent',
                flex: 1,
              }}
            />
          </div>
          {/* Find Events Button (light orange) */}
          <button
            type="submit"
            style={{
              background: '#FFB366',
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.1rem',
              border: 'none',
              borderRadius: 8,
              padding: '0.7rem 2.2rem',
              cursor: 'pointer',
              marginLeft: 8,
              marginRight: 8,
              boxShadow: theme === 'dark' ? '0 2px 8px #222' : '0 2px 8px #ffb36622',
              transition: 'background 0.2s',
            }}
          >
            Find Events
          </button>
        </form>
        {/* Events List */}
        {filteredEvents.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#888', fontSize: '1.2rem' }}>No events found.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '2.5rem',
            }}
          >
            {filteredEvents.map(event => (
              <div
                key={event.id}
                style={{
                  background: theme === 'dark' ? '#23232b' : '#fff',
                  borderRadius: 24,
                  boxShadow: theme === 'dark' ? '0 4px 24px 0 #111' : '0 4px 24px 0 #ff66001a',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 480,
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  cursor: 'pointer',
                  border: theme === 'dark' ? '1.5px solid #333' : '1.5px solid #ffe0cc',
                }}
              >
                {event.image_url && (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    style={{
                      width: '100%',
                      height: 180,
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                )}
                <div style={{ padding: '1.5rem 1.2rem 1.2rem 1.2rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{
                      background: '#b2f0ea',
                      color: '#21706a',
                      borderRadius: 20,
                      padding: '0.3rem 1.1rem',
                      fontWeight: 600,
                      fontSize: '1rem',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                    }}>{event.event_date}</span>
                    {event.location && (
                      <span style={{
                        background: '#f3f4f6',
                        color: '#6b7280',
                        borderRadius: 20,
                        padding: '0.3rem 1.1rem',
                        fontWeight: 500,
                        fontSize: '1rem',
                        whiteSpace: 'nowrap',
                        marginLeft: 0,
                      }}>{event.location}</span>
                    )}
                  </div>
                  <h2 style={{
                    color: theme === 'dark' ? '#fff' : '#222',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                    margin: 0,
                    marginBottom: 8,
                    letterSpacing: '-0.5px',
                  }}>{event.title}</h2>
                  <p style={{
                    color: theme === 'dark' ? '#fff' : '#444',
                    fontSize: '1.08rem',
                    margin: 0,
                    marginBottom: 18,
                    lineHeight: 1.6,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>{event.short_description}</p>
                  <div style={{ marginTop: 'auto' }}>
                    <Link
                      to={`/events/${event.id}`}
                      style={{
                        color: '#0e9488',
                        fontWeight: 600,
                        fontSize: '1.08rem',
                        textDecoration: 'none',
                        display: 'inline-block',
                        marginTop: 8,
                        letterSpacing: '0.2px',
                        transition: 'color 0.2s',
                      }}
                      onMouseOver={e => (e.currentTarget.style.color = '#FF6600')}
                      onMouseOut={e => (e.currentTarget.style.color = '#0e9488')}
                    >
                      Learn More &rarr;
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Events; 